import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import CategoryReferenceGuide from '@/components/CategoryReferenceGuide';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    prefix: '',
    description: '',
    typical_items: '',
    inventory_type: 'both',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?active_only=false`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const typical_items = formData.typical_items
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const payload = {
        ...formData,
        prefix: formData.prefix.toUpperCase().trim(),
        typical_items,
      };
      if (editMode && currentCategory) {
        await axios.patch(`${API}/categories/${currentCategory.id}`, payload);
        toast.success('Category updated successfully!');
      } else {
        await axios.post(`${API}/categories`, payload);
        toast.success('Category created successfully!');
      }
      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setCurrentCategory(category);
    setFormData({
      name: category.name,
      prefix: category.prefix,
      description: category.description || '',
      typical_items: (category.typical_items || []).join('\n'),
      inventory_type: category.inventory_type,
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleToggleActive = async (category) => {
    try {
      await axios.patch(`${API}/categories/${category.id}`, {
        active: !category.active,
      });
      toast.success(`Category ${category.active ? 'deactivated' : 'activated'}`);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? This is only possible if no products use it.')) return;
    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Category deleted successfully!');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      prefix: '',
      description: '',
      typical_items: '',
      inventory_type: 'both',
    });
    setEditMode(false);
    setCurrentCategory(null);
  };

  const getInventoryTypeBadge = (type) => {
    const styles = {
      customer: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
      internal: 'bg-teal-100 text-teal-800 hover:bg-teal-100',
      both: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
    };
    return styles[type] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1680e5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Categories</h1>
          <p className="text-slate-600 mt-2">Manage product categories and SKU prefixes (Admin)</p>
        </div>
        <div className="flex items-center gap-2">
          <CategoryReferenceGuide triggerLabel="View Reference Guide" />
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="add-category-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Category' : 'Add New Category'}</DialogTitle>
              <DialogDescription>
                Define a category with a prefix for auto-generated SKUs
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Category Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Spare Parts"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prefix">SKU Prefix * (1-6 characters, uppercase)</Label>
                <Input
                  id="prefix"
                  placeholder="e.g., SP"
                  value={formData.prefix}
                  onChange={(e) =>
                    setFormData({ ...formData, prefix: e.target.value.toUpperCase() })
                  }
                  maxLength={6}
                  required
                  data-testid="category-prefix-input"
                />
                <p className="text-xs text-slate-500">
                  SKUs will be generated as {formData.prefix || 'XX'}-0001, {formData.prefix || 'XX'}-0002, etc.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="inventory_type">Applicable To *</Label>
                <Select
                  value={formData.inventory_type}
                  onValueChange={(value) => setFormData({ ...formData, inventory_type: value })}
                >
                  <SelectTrigger data-testid="category-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer Inventory Only</SelectItem>
                    <SelectItem value="internal">Internal Materials Only</SelectItem>
                    <SelectItem value="both">Both Types</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="typical_items">Typical Items (one per line)</Label>
                <Textarea
                  id="typical_items"
                  placeholder={"Helmets\nGloves\nKnee Pads"}
                  rows={5}
                  value={formData.typical_items}
                  onChange={(e) => setFormData({ ...formData, typical_items: e.target.value })}
                  data-testid="category-typical-items-input"
                />
                <p className="text-xs text-slate-500">
                  These help operators know what fits in this category (shown in the reference guide).
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1680e5] hover:bg-[#1680e5]/90"
                data-testid="category-submit-button"
              >
                {editMode ? 'Update Category' : 'Create Category'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Tag className="h-5 w-5 text-[#1680e5]" />
            How Categories & Auto-SKUs Work
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm text-slate-700">
          <p>• Each category has a unique <strong>prefix</strong> (e.g., &quot;SP&quot; for Spare Parts)</p>
          <p>• When operators create products, they select a category from a dropdown</p>
          <p>• SKUs are auto-generated as <code className="bg-white px-1 rounded">PREFIX-0001</code>, <code className="bg-white px-1 rounded">PREFIX-0002</code>, etc.</p>
          <p>• Numbers are unique per prefix (each category has its own counter)</p>
          <p>• Categories can be limited to Customer Inventory, Internal Materials, or both</p>
        </CardContent>
      </Card>

      {categories.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Tag className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No categories yet</h3>
            <p className="text-slate-600 mb-4">Create your first category to start using auto-SKUs</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-[#1680e5] hover:bg-[#1680e5]/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>All Categories</CardTitle>
            <CardDescription>{categories.length} categor(ies) configured</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Applies To</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id} className="table-row-hover">
                    <TableCell className="font-medium">{category.name}</TableCell>
                    <TableCell>
                      <code className="font-mono text-sm bg-slate-100 px-2 py-1 rounded font-semibold">
                        {category.prefix}-####
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge className={`capitalize ${getInventoryTypeBadge(category.inventory_type)}`}>
                        {category.inventory_type === 'both' ? 'All' : category.inventory_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                      {category.description || '-'}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={category.active}
                        onCheckedChange={() => handleToggleActive(category)}
                        data-testid={`toggle-active-${category.prefix}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(category)}
                          data-testid={`edit-category-${category.prefix}`}
                        >
                          <Edit className="h-4 w-4 text-[#1680e5]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(category.id)}
                          data-testid={`delete-category-${category.prefix}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
